import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { X, Minus, Send, MessageCircle, FileText, Download } from "lucide-react";
import { toast } from "sonner";
import ImageLightbox from "./chat/ImageLightbox";
import { cn } from "../lib/utils";

const API = process.env.REACT_APP_BACKEND_URL;

const STORAGE_KEY = "avida.floating-chats.open";

// Cache of conversation summaries we've already looked up
const convoCache = {};
async function fetchConvo(id) {
  if (convoCache[id]) return convoCache[id];
  const t = localStorage.getItem("token");
  if (!t) return null;
  try {
    // /api/im/conversations returns the user's full list — pick the one we want
    const r = await fetch(`${API}/api/im/conversations`, { headers: { Authorization: `Bearer ${t}` } });
    if (!r.ok) return null;
    const list = await r.json();
    list.forEach(c => { convoCache[c.id] = c; });
    return convoCache[id] || null;
  } catch (_e) { return null; }
}

/**
 * Global floating-chat provider. Mounted once at the app root.
 * Other components open a chat by dispatching:
 *   window.dispatchEvent(new CustomEvent("avida:open-floating-chat", { detail: convo }))
 *
 * The component holds its own WS connection so chats stay live as the user
 * navigates around the app.
 */
export default function FloatingChats() {
  const [openChats, setOpenChats] = useState([]);  // [{id, name, type, other_user, minimized}]
  const [user, setUser] = useState(null);
  const [messagesByConvo, setMessagesByConvo] = useState({});
  const wsRef = useRef(null);
  const openChatsRef = useRef([]);
  useEffect(() => { openChatsRef.current = openChats; }, [openChats]);

  // Load logged-in user — token-aware. The old version fetched once at
  // app mount, so logging in WITHOUT a full page reload left `user` null
  // and the global chat socket never connected (PCW portal landing bug).
  useEffect(() => {
    let cancelled = false;
    let knownToken = null;
    const load = async () => {
      const t = localStorage.getItem("token");
      if (t === knownToken) return;
      knownToken = t;
      if (!t) { if (!cancelled) setUser(null); return; }
      try {
        const r = await fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${t}` } });
        const d = r.ok ? await r.json() : null;
        if (!cancelled && d) setUser(d);
      } catch (_e) { /* retry on next tick */ }
    };
    load();
    const iv = setInterval(load, 5000);
    const onStorage = (e) => { if (e.key === "token") load(); };
    window.addEventListener("storage", onStorage);
    return () => {
      cancelled = true;
      clearInterval(iv);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  // Persisted open chats
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      if (Array.isArray(stored) && stored.length) setOpenChats(stored);
    } catch (_e) { /* noop */ }
  }, []);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(openChats.slice(0, 4)));
  }, [openChats]);

  // Request browser Notification permission once
  useEffect(() => {
    if (!user || !("Notification" in window)) return;
    if (Notification.permission === "default") {
      // Request after a short delay so it doesn't fire on first paint
      const id = setTimeout(() => {
        try { Notification.requestPermission(); } catch (_e) {}
      }, 4000);
      return () => clearTimeout(id);
    }
  }, [user]);

  // WebSocket — one shared connection, auto-reconnecting with keepalive.
  // Previously this socket connected ONCE and never came back after a
  // drop (ingress idle-kills sockets after ~60s of silence), so users
  // silently stopped receiving chat notifications for the whole session.
  useEffect(() => {
    if (!user) return;

    let alive = true;
    let reconnectTimer = null;
    let heartbeatTimer = null;
    let attempts = 0;
    let lastPong = Date.now();

    const connect = () => {
      if (!alive) return;
      const wsUrl = API.replace(/^http/, "ws");
      const ws = new WebSocket(`${wsUrl}/api/im/ws`);
      wsRef.current = ws;

      ws.onopen = () => {
        attempts = 0;
        lastPong = Date.now();
        if (heartbeatTimer) clearInterval(heartbeatTimer);
        heartbeatTimer = setInterval(() => {
          if (ws.readyState !== WebSocket.OPEN) return;
          // 3 missed pongs => socket is silently dead; force a reconnect
          if (Date.now() - lastPong > 75000) {
            try { ws.close(); } catch (_e) { /* noop */ }
            return;
          }
          try { ws.send(JSON.stringify({ type: "ping", ts: Date.now() })); } catch (_e) { /* noop */ }
        }, 25000);
      };

      ws.onmessage = async (ev) => {
      try {
        const data = JSON.parse(ev.data);
        if (data.type === "pong") { lastPong = Date.now(); return; }
        if (data.type === "message" && data.message) {
          const msg = data.message;
          const convoId = msg.conversation_id;
          setMessagesByConvo(prev => ({
            ...prev,
            [convoId]: [...(prev[convoId] || []), msg].slice(-100),
          }));

          // ── Reply-from-anywhere toast ─────────────────────────
          // Skip if it's our own message
          if (msg.sender_id === user.id) return;
          // Skip if user is on /messages page (Messages.jsx will handle in-app toast)
          if (window.location.pathname.startsWith("/messages")) return;
          // Skip if the persistent Comms Dock is already showing this convo
          if (window.__comms_dock_open_convo === convoId) return;

          // Skip if a floating panel is open & visible for this convo
          const existing = openChatsRef.current.find(c => c.id === convoId);
          if (existing && !existing.minimized) return;

          const convo = await fetchConvo(convoId);
          const titleName = convo
            ? (convo.name || (convo.other_user ? `${convo.other_user.first_name || ""} ${convo.other_user.last_name || ""}`.trim() : "Team Chat"))
            : (msg.sender_name || "New message");
          const preview = (msg.text || "[attachment]").slice(0, 80);

          toast.message(titleName, {
            description: `${msg.sender_name}: ${preview}`,
            duration: 8000,
            action: {
              label: "Reply",
              onClick: () => {
                if (convo) {
                  window.dispatchEvent(new CustomEvent("avida:open-floating-chat", { detail: convo }));
                }
              },
            },
          });

          // Native browser notification when tab is hidden
          if (document.hidden && "Notification" in window && Notification.permission === "granted") {
            try {
              const n = new Notification(titleName, {
                body: `${msg.sender_name}: ${preview}`,
                icon: "/favicon.ico",
                tag: `avida-msg-${convoId}`,  // collapse multiple from same convo
                renotify: true,
              });
              n.onclick = () => {
                window.focus();
                if (convo) {
                  window.dispatchEvent(new CustomEvent("avida:open-floating-chat", { detail: convo }));
                }
                n.close();
              };
            } catch (_e) { /* noop */ }
          }
        }
      } catch (_e) { /* noop */ }
    };
      ws.onclose = (ev) => {
        wsRef.current = null;
        if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null; }
        if (!alive || ev?.code === 4001) return;
        const delay = Math.min(1000 * Math.pow(2, Math.min(attempts, 5)), 15000);
        attempts += 1;
        reconnectTimer = setTimeout(connect, delay);
      };
      ws.onerror = () => { try { ws.close(); } catch (_e) { /* noop */ } };
    };

    connect();

    return () => {
      alive = false;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (heartbeatTimer) clearInterval(heartbeatTimer);
      if (wsRef.current) { try { wsRef.current.close(); } catch (_e) { /* noop */ } }
    };
  }, [user]);

  // Listen for open-chat events
  useEffect(() => {
    const onOpen = (e) => {
      const convo = e.detail;
      if (!convo) return;
      setOpenChats(prev => {
        if (prev.find(c => c.id === convo.id)) {
          // Already open: un-minimize
          return prev.map(c => c.id === convo.id ? { ...c, minimized: false } : c);
        }
        return [...prev.slice(-3), { ...convo, minimized: false }];
      });
      // Pre-load messages
      const t = localStorage.getItem("token");
      if (t) {
        fetch(`${API}/api/im/conversations/${convo.id}/messages?limit=50`, {
          headers: { Authorization: `Bearer ${t}` },
        }).then(r => r.ok ? r.json() : []).then(msgs => {
          setMessagesByConvo(prev => ({ ...prev, [convo.id]: Array.isArray(msgs) ? msgs : (msgs.items || []) }));
        });
      }
    };
    window.addEventListener("avida:open-floating-chat", onOpen);
    return () => window.removeEventListener("avida:open-floating-chat", onOpen);
  }, []);

  const closeChat = (id) => setOpenChats(prev => prev.filter(c => c.id !== id));
  const toggleMin = (id) => setOpenChats(prev => prev.map(c => c.id === id ? { ...c, minimized: !c.minimized } : c));

  if (!user || openChats.length === 0) return null;

  return (
    <div
      className="fixed bottom-0 right-4 z-[60] flex items-end gap-3 pointer-events-none"
      data-testid="floating-chats-root"
    >
      {openChats.map((convo) => (
        <FloatingChatPanel
          key={convo.id}
          convo={convo}
          messages={messagesByConvo[convo.id] || []}
          user={user}
          ws={wsRef.current}
          onClose={() => closeChat(convo.id)}
          onToggleMinimize={() => toggleMin(convo.id)}
        />
      ))}
    </div>
  );
}

function FloatingChatPanel({ convo, messages, user, ws, onClose, onToggleMinimize }) {
  const [draft, setDraft] = useState("");
  const [gallery, setGallery] = useState(null); // { index }
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const fileUrl = (storagePath) =>
    `${API}/api/im/files/${storagePath}?auth=${localStorage.getItem("token") || ""}`;

  // Swipe-through gallery of every image in this floating thread.
  const galleryImages = useMemo(
    () => messages
      .filter((m) => m.attachment?.is_image && m.attachment?.storage_path)
      .map((m) => ({ id: m.id, src: fileUrl(m.attachment.storage_path), filename: m.attachment.filename })),
    [messages]
  );
  const openImage = (m) =>
    setGallery({ index: Math.max(0, galleryImages.findIndex((g) => g.id === m.id)) });

  useEffect(() => {
    if (scrollRef.current && !convo.minimized) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, convo.minimized]);

  const send = useCallback(() => {
    if (!draft.trim() || !ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({
      type: "message",
      conversation_id: convo.id,
      text: draft.trim(),
    }));
    setDraft("");
  }, [draft, ws, convo.id]);

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const titleName = convo.name || (convo.other_user ? `${convo.other_user.first_name || ""} ${convo.other_user.last_name || ""}`.trim() : convo.id?.slice(0, 6));
  const initials = (titleName || "?").split(/\s+/).map(s => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

  // Minimized: 56×56 round bubble
  if (convo.minimized) {
    return (
      <button
        onClick={onToggleMinimize}
        className="pointer-events-auto w-14 h-14 rounded-full shadow-2xl flex items-center justify-center font-bold text-sm relative mb-3"
        style={{ background: "linear-gradient(135deg, #003366 0%, #00467a 100%)", color: "#FFDA00" }}
        title={`Open chat with ${titleName}`}
        data-testid={`floating-bubble-${convo.id}`}
      >
        {initials}
        <span
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:bg-red-50"
          title="Close"
        >
          <X className="w-3 h-3 text-slate-500" />
        </span>
      </button>
    );
  }

  return (
    <div
      className="pointer-events-auto w-[320px] h-[420px] rounded-t-2xl shadow-2xl flex flex-col bg-white border border-slate-200 overflow-hidden"
      data-testid={`floating-panel-${convo.id}`}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 cursor-pointer" style={{ background: "linear-gradient(135deg, #003366 0%, #00467a 100%)" }} onClick={onToggleMinimize}>
        <div className="w-7 h-7 rounded-full flex items-center justify-center font-bold text-[10px]" style={{ background: "#FFDA00", color: "#003366" }}>
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold text-white truncate">{titleName}</p>
          <p className="text-[10px] text-yellow-200/80">{convo.type === "direct" ? "Direct message" : "Channel"}</p>
        </div>
        <button onClick={(e) => { e.stopPropagation(); onToggleMinimize(); }} className="p-1 rounded hover:bg-white/10" title="Minimize">
          <Minus className="w-3.5 h-3.5 text-white/80" />
        </button>
        <button onClick={(e) => { e.stopPropagation(); onClose(); }} className="p-1 rounded hover:bg-white/10" title="Close">
          <X className="w-3.5 h-3.5 text-white/80" />
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5 bg-slate-50">
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-slate-300">
            <MessageCircle className="w-8 h-8 mb-2" />
            <p className="text-xs">No messages yet</p>
          </div>
        ) : (
          messages.map((m) => {
            const isMine = m.sender_id === user?.id;
            return (
              <div key={m.id} className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
                <div
                  className="max-w-[80%] px-2.5 py-1.5 rounded-2xl text-[12px] leading-snug break-words"
                  style={{
                    background: isMine ? "linear-gradient(135deg, #003366 0%, #00467a 100%)" : "#FFFFFF",
                    color: isMine ? "#FFFFFF" : "#1e293b",
                    border: isMine ? "1px solid #003366" : "1px solid #e2e8f0",
                    borderTopRightRadius: isMine ? "4px" : "16px",
                    borderTopLeftRadius: isMine ? "16px" : "4px",
                  }}
                >
                  {!isMine && convo.type !== "direct" && (
                    <p className="text-[9px] font-semibold mb-0.5" style={{ color: "#003366" }}>{m.sender_name}</p>
                  )}
                  {m.text && <span>{m.text}</span>}
                  {m.attachment && (
                    m.attachment.is_image ? (
                      <button
                        type="button"
                        onClick={() => openImage(m)}
                        className={cn("block rounded-lg overflow-hidden border", m.text ? "mt-1.5" : "")}
                        style={{ borderColor: isMine ? "rgba(255,255,255,0.25)" : "#e2e8f0" }}
                        title="Click to view full size"
                        data-testid={`floating-img-${m.id}`}
                      >
                        <img
                          src={fileUrl(m.attachment.storage_path)}
                          alt={m.attachment.filename}
                          className="w-full max-w-[220px] max-h-52 object-cover cursor-zoom-in hover:opacity-95 transition-opacity"
                          loading="lazy"
                        />
                      </button>
                    ) : (
                      <a
                        href={fileUrl(m.attachment.storage_path)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cn("inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg border max-w-[220px]", m.text ? "mt-1.5" : "")}
                        style={{
                          background: isMine ? "rgba(255,255,255,0.12)" : "#f8fafc",
                          borderColor: isMine ? "rgba(255,255,255,0.25)" : "#e2e8f0",
                        }}
                        data-testid={`floating-file-${m.id}`}
                      >
                        <FileText className="w-5 h-5 shrink-0" style={{ color: isMine ? "#FFDA00" : "#fdda0d" }} />
                        <span className="text-[11px] truncate flex-1" style={{ color: isMine ? "#fff" : "#475569" }}>
                          {m.attachment.filename || "Attachment"}
                        </span>
                        <Download className="w-3.5 h-3.5 shrink-0 opacity-70" />
                      </a>
                    )
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Input */}
      <div className="border-t border-slate-200 p-2 bg-white flex items-center gap-2">
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
          placeholder="Type a message…"
          className="flex-1 text-[12px] px-3 py-1.5 rounded-full bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-100"
          data-testid={`floating-input-${convo.id}`}
        />
        <button
          onClick={send}
          disabled={!draft.trim()}
          className="w-7 h-7 rounded-full flex items-center justify-center disabled:opacity-30"
          style={{ background: "#003366", color: "#FFDA00" }}
          data-testid={`floating-send-${convo.id}`}
        >
          <Send className="w-3.5 h-3.5" />
        </button>
      </div>

      {gallery && (
        <ImageLightbox images={galleryImages} startIndex={gallery.index} onClose={() => setGallery(null)} />
      )}
    </div>
  );
}
